import { isAxiosError } from "axios";
import type { AssetWritePayload } from "@/modules/inventory/services/engineApi";
import type { Engine } from "@/modules/inventory/types/engine.types";
import type { Trailer } from "@/modules/inventory/types/trailer.types";
import type { PermissionName } from "@/modules/tenant/types/permissions";

/** Sentinel for "no selection" in Select fields (Radix rejects empty values). */
export const NONE = "__none__";

export type AssetType = "engine" | "trailer";
export type FieldKind = "text" | "number" | "select";

export interface FieldDef {
  key: string;
  label: string;
  kind: FieldKind;
  options?: readonly { value: string; label: string }[];
  /**
   * ACL-09 (#1408, D-08) — a field the viewer must hold this permission to
   * see. It is not rendered, seeded or posted without it.
   */
  requiresPermission?: PermissionName;
}

export const ENGINE_FIELDS: FieldDef[] = [
  { key: "engine_manufacturer", label: "Manufacturer", kind: "text" },
  { key: "engine_model", label: "Model", kind: "text" },
  { key: "engine_horsepower", label: "Horsepower", kind: "number" },
  { key: "engine_year", label: "Year", kind: "number" },
  { key: "engine_hours", label: "Hours", kind: "number" },
  { key: "engine_serial_number", label: "Serial Number", kind: "text" },
  { key: "drive_type", label: "Drive Type", kind: "select" },
  { key: "fuel_type", label: "Fuel Type", kind: "select" },
];

export const TRAILER_FIELDS: FieldDef[] = [
  { key: "trailer_make", label: "Make", kind: "text" },
  { key: "trailer_model", label: "Model", kind: "text" },
  { key: "trailer_vin", label: "VIN", kind: "text" },
  { key: "trailer_axles", label: "Axles", kind: "select" },
  { key: "trailer_gvwr", label: "GVWR (lb)", kind: "number" },
  { key: "shipping_weight", label: "Shipping Weight (lb)", kind: "number" },
];

export function detailFieldsFor(type: AssetType): FieldDef[] {
  return type === "engine" ? ENGINE_FIELDS : TRAILER_FIELDS;
}

/**
 * SP4 slice 1 (D3) — the rest of the 7-field pricing card (our_price keeps
 * its dedicated "Price" handling). Retail + cost_price live on
 * inventory_items; invoice/freight on the asset detail tables. Shared by
 * both asset types, rendered after our_price in Inventory & Pricing.
 */
export const PRICING_FIELDS: FieldDef[] = [
  { key: "msrp", label: "MSRP", kind: "number" },
  { key: "regular_price", label: "Regular Price", kind: "number" },
  { key: "sale_price", label: "Sale Price", kind: "number" },
  { key: "cost_price", label: "Cost", kind: "number", requiresPermission: 'finance.cost.view' },
  { key: "invoice", label: "Invoice", kind: "number", requiresPermission: 'finance.cost.view' },
  { key: "freight", label: "Freight", kind: "number", requiresPermission: 'finance.cost.view' },
];

/**
 * ACL-09 (#1408, D-08/D-09) — the pricing fields this viewer may see. Without
 * finance.cost.view the cost, invoice and freight fields are dropped, so they
 * are never rendered, seeded from a (possibly stale) cached asset, or posted.
 */
export function pricingFieldsFor(canViewCost: boolean): FieldDef[] {
  return canViewCost ? PRICING_FIELDS : PRICING_FIELDS.filter((field) => !field.requiresPermission);
}

/**
 * Boat-canvas-style section grouping for the AssetCanvas (user-approved
 * 3-section layout): Identity / Specifications, each rendering its detail
 * fields in a 2-col grid. The canvas itself prepends the Condition toggle to
 * Identity and appends the shared Inventory & Pricing section (stock #,
 * price, status, location).
 */
export interface AssetSectionDef {
  id: string;
  title: string;
  /** Detail-field keys (resolved against detailFieldsFor) in render order. */
  keys: string[];
}

export function sectionsFor(type: AssetType): AssetSectionDef[] {
  return type === "engine"
    ? [
        {
          id: "identity",
          title: "Identity",
          keys: ["engine_manufacturer", "engine_model", "engine_year", "engine_serial_number"],
        },
        {
          id: "specs",
          title: "Specifications",
          keys: ["engine_horsepower", "engine_hours", "drive_type", "fuel_type"],
        },
      ]
    : [
        {
          id: "identity",
          title: "Identity",
          keys: ["trailer_make", "trailer_model", "trailer_vin"],
        },
        {
          id: "specs",
          title: "Specifications",
          keys: ["trailer_axles", "trailer_gvwr", "shipping_weight"],
        },
      ];
}

export type AssetFormValues = Record<string, string>;

/**
 * Seed form values from an existing asset (edit) or blank (create). Money
 * fields are seeded only when `canViewCost` (ACL-09).
 */
export function initialValues(
  type: AssetType,
  initial: (Engine | Trailer) | null | undefined,
  canViewCost: boolean,
): AssetFormValues {
  const source = (initial ?? {}) as Record<string, unknown>;
  const values: AssetFormValues = {
    inventory_number: String(source.inventory_number ?? ""),
    condition: String(source.condition ?? NONE),
    our_price: source.our_price != null ? String(source.our_price) : "",
    status_id: String(source.status_id ?? NONE),
    location_id: String(
      (source.locations as { id: string; is_primary: boolean }[] | undefined)?.find(
        (l) => l.is_primary,
      )?.id ?? NONE,
    ),
  };
  for (const field of [...detailFieldsFor(type), ...pricingFieldsFor(canViewCost)]) {
    const raw = source[field.key];
    values[field.key] = raw != null ? String(raw) : field.kind === "select" ? NONE : "";
  }
  return values;
}

/**
 * Form values → write-endpoint payload (NONE/blank → null, numbers parsed).
 * Money keys are posted only when `canViewCost`: a non-holder's save never
 * carries cost_price, invoice or freight (ACL-09; the API refuses them).
 */
export function buildPayload(
  type: AssetType,
  values: AssetFormValues,
  canViewCost: boolean,
): AssetWritePayload {
  const payload: AssetWritePayload = {
    inventory_number: values.inventory_number.trim(),
    condition: values.condition === NONE ? null : values.condition,
    our_price: values.our_price.trim() === "" ? null : Number(values.our_price),
    status_id: values.status_id === NONE ? null : values.status_id,
    location_id: values.location_id === NONE ? null : values.location_id,
  };
  for (const field of [...detailFieldsFor(type), ...pricingFieldsFor(canViewCost)]) {
    const raw = values[field.key];
    if (field.kind === "select") {
      payload[field.key] = raw === NONE ? null : raw;
    } else if (field.kind === "number") {
      payload[field.key] = raw.trim() === "" ? null : Number(raw);
    } else {
      payload[field.key] = raw.trim() === "" ? null : raw.trim();
    }
  }
  return payload;
}

export function firstValidationError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as Record<string, string[]> | undefined;
    const first = errors ? Object.values(errors)[0]?.[0] : undefined;
    return first ?? error.response?.data?.message ?? fallback;
  }
  return fallback;
}
